import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import CustomDatePicker from 'Components/CustomDatePicker'
import CustomTablePagination from 'Components/CustomTablePagination'
import EnhancedTableHead, { type HeadCell } from 'Components/EnhancedTableHead/EnhancedTableHead'
import ListSearch from 'Components/ListSearch'
import NoWrapTableCell from 'Components/NoWrapTableCell'
import PageTitle from 'Components/PageTitle'
import StyledAccordion from 'Components/StyledAccordion'
import StyledLink from 'Components/StyledLink'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import { useGetWebShopOrderFilterOptionsQuery } from 'Services/SalesManagement/Features/Order/orderApi'
import {
  fetchWebShopOrderList,
  type WebShopOrderParamShape
} from 'Services/SalesManagement/Features/Order/orderSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import {
  getFilterByDefaultDate,
  ROWS_PER_PAGE_DEFAULT,
  SALES_MANAGEMENT_ROOT_URL
} from 'Util/constants'
import {
  dateFormatter,
  datetimeFormatter,
  replaceHyphenWithSpace,
  showValueOrHyphen
} from 'Util/formatters'
import useQuery from 'Util/getQueryParamsHook'
import ErrorIcon from '@mui/icons-material/ErrorOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FilterListIcon from '@mui/icons-material/FilterList'
import {
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  type SelectChangeEvent,
  type Theme
} from '@mui/material'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import { useTheme } from '@mui/material/styles'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { unwrapResult } from '@reduxjs/toolkit'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

function getNoteValue(value: [number, string], theme: Theme) {
  const warningLevelColors = [
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.info.main
  ]
  const warningLevel = value[0]
  const noteText = value[1]
  if (noteText === '') return '-'
  return (
    <Tooltip title={noteText}>
      <IconButton sx={{ padding: 0 }}>
        <ErrorIcon sx={{ color: warningLevelColors[warningLevel - 1] }} />
      </IconButton>
    </Tooltip>
  )
}

export default function WebShopOrderListPage() {
  const baseUrl = `${SALES_MANAGEMENT_ROOT_URL}/orders/webshop-orders`
  const dispatch = useAppDispatch()
  const queryParams = useQuery()
  const navigate = useNavigate()
  const theme = useTheme()

  const { webShopOrders: rows, count, loading } = useTypedSelector(state => state.order)
  const { authorities } = useAuthorityContext()

  const [searchText, setSearchText] = useState(queryParams.get('search') ?? '')

  const [filters, setFilters] = useState<Partial<WebShopOrderParamShape>>({
    status: queryParams.get('status') ?? '',
    authority_id: queryParams.get('authority_id') ?? '',
    payment_method_id: queryParams.get('payment_method_id') ?? '',
    delivery_status: queryParams.get('delivery_status') ?? '',
    created_after: queryParams.get('created_after') ?? dateFormatter(getFilterByDefaultDate()),
    created_before: queryParams.get('created_before') ?? ''
  })

  const [params, setParams] = useState<WebShopOrderParamShape>({
    ordering: queryParams.get('ordering') ?? '-created',
    search: queryParams.get('search') ?? '',
    page: queryParams.get('page') ? parseInt(queryParams.get('page') as string) : 1,
    status: queryParams.get('status') ?? '',
    authority_id: queryParams.get('authority_id') ?? '',
    payment_method_id: queryParams.get('payment_method_id') ?? '',
    delivery_status: queryParams.get('delivery_status') ?? '',
    created_after: queryParams.get('created_after') ?? dateFormatter(getFilterByDefaultDate()),
    created_before: queryParams.get('created_before') ?? ''
  })

  useEffect(() => {
    dispatch(fetchWebShopOrderList(params))
      .then(unwrapResult)
      .then(() => {
        setQueryParams()
      })
  }, [dispatch, params])

  const { data: webShopOrderFilterChoices, isFetching: isWebShopOrderFilterOptionsFetching } =
    useGetWebShopOrderFilterOptionsQuery()

  const setQueryParams = () => {
    const query: string[] = []
    Object.keys(params).forEach((key: string) => {
      const value = params[key as keyof WebShopOrderParamShape]
      if (value) {
        query.push(key + '=' + value)
      }
    })
    const queryString = query.join('&')

    navigate({
      pathname: location.pathname,
      search: '?' + queryString
    })
  }

  const handleRequestSort = (_event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = params.ordering.includes('-')
    setParams(prevState => ({
      ...prevState,
      ordering: `${isAsc ? '' : '-'}${property}`
    }))
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({ ...prevState, page: newPage + 1 }))
  }

  const handleSearch = () => {
    setParams(prevState => ({ ...prevState, search: searchText, page: 1 }))
  }

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const handleSelectChange = (event: SelectChangeEvent, name: keyof WebShopOrderParamShape) => {
    const { value } = event.target
    const newValue = params[name] === value ? '' : value
    setFilters(prevState => ({ ...prevState, [name]: newValue }))
  }

  const handleDatePickerChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = dateFormatter(value).toString()
    }
    setFilters(prevState => ({ ...prevState, [name]: formattedValue }))
  }

  const submitFilters = () => {
    setParams(prevState => ({ ...prevState, ...filters, page: 1 }))
  }

  const clearFilters = () => {
    setFilters(prevState => ({
      ...prevState,
      status: '',
      authority_id: '',
      payment_method_id: '',
      delivery_status: '',
      created_after: '',
      created_before: ''
    }))
  }

  const breadcrumbs = [
    {
      title: t`Home`,
      link: SALES_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Orders`
    },
    {
      title: t`Waltti webshop orders`
    }
  ]

  const headCells: HeadCell[] = [
    {
      id: 'transactional_id',
      numeric: false,
      disablePadding: false,
      label: t`Transactional ID`,
      sortable: false
    },
    {
      id: 'contact_email',
      numeric: false,
      disablePadding: false,
      label: t`Contact email`,
      sortable: false
    },
    {
      id: 'payment_date',
      numeric: false,
      disablePadding: false,
      label: t`Payment date`,
      sortable: true
    },
    {
      id: 'created',
      numeric: false,
      disablePadding: false,
      label: t`Created`,
      sortable: true
    },
    {
      id: 'status',
      numeric: false,
      disablePadding: false,
      label: t`Status`,
      sortable: true
    },
    {
      id: 'authority_id',
      numeric: false,
      disablePadding: false,
      label: t`Authority`,
      sortable: false
    },
    {
      id: 'payment_method_name',
      numeric: false,
      disablePadding: false,
      label: t`Payment method`,
      sortable: true
    },
    {
      id: 'note',
      numeric: false,
      disablePadding: false,
      label: t`Note`,
      sortable: false
    }
  ]

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <BackButton link={SALES_MANAGEMENT_ROOT_URL} />
        <PageTitle>{t`Waltti webshop orders`}</PageTitle>
        <Typography component="p" variant="subtitle1" sx={{ fontSize: '15px', mb: 4 }}>
          {t`In the Waltti webshop orders section you can search for, filter and sort the orders made in the Waltti webshop. The order listing below is refreshed at regular intervals, so note that the latest orders are not listed here in real-time and the changes made to a single order are not updated in this list in real-time. Also note that by default the orders are filtered by the last year.`}
        </Typography>
        <ListSearch
          searchText={searchText}
          handleSearchTextChange={handleSearchTextChange}
          handleSearch={handleSearch}
          placeholder={t`Search for Waltti webshop orders`}
          helperText={t`Search for travel card, transactional ID, email address, or Waltti ID`}
        />
      </Box>
      <StyledAccordion sx={{ mb: 3 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <FilterListIcon sx={{ mr: 1 }} color="primary" />
          <Typography component="span">{t`Filters`}</Typography>
        </AccordionSummary>
        <Divider />
        {isWebShopOrderFilterOptionsFetching ? (
          <Box>
            <LinearProgress />
          </Box>
        ) : (
          <AccordionDetails>
            <Box sx={{ mb: 3 }}>
              <Typography
                component="h3"
                variant="h3"
                sx={{ my: 2 }}
              >{t`Filter by time range`}</Typography>
              <Box sx={{ display: 'flex', mb: 6 }}>
                <CustomDatePicker
                  onChange={handleDatePickerChange}
                  name="created_after"
                  value={filters.created_after ? new Date(filters.created_after) : null}
                  dateToCompare={filters.created_before ? new Date(filters.created_before) : null}
                  dateToCompareFieldName="created_before"
                  isStartDate={true}
                  label={t`Start date`}
                />
                <Box sx={{ p: 2 }}>{` - `}</Box>
                <CustomDatePicker
                  onChange={handleDatePickerChange}
                  name="created_before"
                  value={filters.created_before ? new Date(filters.created_before) : null}
                  dateToCompare={filters.created_after ? new Date(filters.created_after) : null}
                  dateToCompareFieldName="created_after"
                  isStartDate={false}
                  label={t`End date`}
                />
              </Box>
            </Box>
            <Box sx={{ mb: 3 }}>
              <Typography
                component="h3"
                variant="h3"
                sx={{ my: 2 }}
              >{t`Filter by fields`}</Typography>
              <FormControl sx={{ mr: 1, minWidth: 180, mb: 1 }} size="small">
                <InputLabel>{t`Status`}</InputLabel>
                <Select
                  sx={{ backgroundColor: 'background.default' }}
                  value={filters.status}
                  label={t`Status`}
                  autoWidth
                  onChange={e => handleSelectChange(e, 'status')}
                >
                  {webShopOrderFilterChoices?.field_choices.status.map(option => (
                    <MenuItem key={option[0]} value={option[0]}>
                      {option[1]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ mr: 1, minWidth: 180, mb: 1 }} size="small">
                <InputLabel>{t`Authority`}</InputLabel>
                <Select
                  sx={{ backgroundColor: 'background.default' }}
                  value={filters.authority_id}
                  label={t`Authority`}
                  autoWidth
                  onChange={e => handleSelectChange(e, 'authority_id')}
                >
                  {authorities?.map(({ code, name }) => (
                    <MenuItem key={code} value={code}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ mr: 1, minWidth: 180, mb: 1 }} size="small">
                <InputLabel>{t`Payment method`}</InputLabel>
                <Select
                  sx={{ backgroundColor: 'background.default' }}
                  value={filters.payment_method_id}
                  label={t`Payment method`}
                  autoWidth
                  onChange={e => handleSelectChange(e, 'payment_method_id')}
                >
                  {webShopOrderFilterChoices?.field_choices.payment_method_id.map(option => (
                    <MenuItem key={option[0]} value={option[0]}>
                      {option[1]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ mr: 1, minWidth: 180, mb: 1 }} size="small">
                <InputLabel>{t`Note`}</InputLabel>
                <Select
                  sx={{ backgroundColor: 'background.default' }}
                  value={filters.delivery_status}
                  label={t`Note`}
                  autoWidth
                  onChange={e => handleSelectChange(e, 'delivery_status')}
                >
                  {webShopOrderFilterChoices?.field_choices.delivery_status.map(option => (
                    <MenuItem key={option[0]} value={option[0]}>
                      {option[1]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ mt: 3 }}>
                <CustomButton sx={{ mr: 2 }} onClick={submitFilters}>{t`Filter`}</CustomButton>
                <Button onClick={clearFilters}>{t`Clear filters`}</Button>
              </Box>
            </Box>
          </AccordionDetails>
        )}
      </StyledAccordion>
      <WithLoader loading={loading === 'pending'} rows={ROWS_PER_PAGE_DEFAULT} height={33}>
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer>
            <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="small">
              <EnhancedTableHead
                orderBy={params.ordering}
                headCells={headCells}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {rows.map(row => {
                  return (
                    <StyledTableRow hover key={`${row.transactional_id}_${row.created}`}>
                      <NoWrapTableCell title={row.transactional_id} component="th" scope="row">
                        <StyledLink to={`${baseUrl}/${row.id}/`}>{row.transactional_id}</StyledLink>
                      </NoWrapTableCell>
                      <NoWrapTableCell title={row.contact_email}>
                        {row.contact_email}
                      </NoWrapTableCell>
                      <NoWrapTableCell title={datetimeFormatter(row.payment_date)}>
                        {datetimeFormatter(row.payment_date)}
                      </NoWrapTableCell>
                      <NoWrapTableCell title={datetimeFormatter(row.created)}>
                        {datetimeFormatter(row.created)}
                      </NoWrapTableCell>
                      <NoWrapTableCell title={replaceHyphenWithSpace(row.status)}>
                        {replaceHyphenWithSpace(row.status)}
                      </NoWrapTableCell>
                      <NoWrapTableCell
                        title={`${row.authority_name} (${row.authority_code})`}
                      >{`${row.authority_name} (${row.authority_code})`}</NoWrapTableCell>
                      <NoWrapTableCell title={showValueOrHyphen(row.payment_method_name)}>
                        {showValueOrHyphen(row.payment_method_name)}
                      </NoWrapTableCell>
                      <TableCell>{getNoteValue(row.note, theme)}</TableCell>
                    </StyledTableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={count}
            page={params.page - 1}
            onPageChange={handleChangePage}
          />
        </Paper>
      </WithLoader>
    </Box>
  )
}
